import {type FC, type PropsWithChildren, memo, useRef} from 'react';

import {
    type KeyboardShortcutDescriptor,
} from 'components/keyboard_shortcuts/keyboard_shortcuts_sequence';
import {Tooltip} from '@time-webkit/all/atoms/tooltip';
import {type TooltipTriggerProps, useTooltipTriggerState} from '@react-stately/tooltip';
import {useTooltipTrigger} from '@react-aria/tooltip';

import {ShortcutKey, ShortcutKeyVariant} from 'components/shortcut_key';
import {
    useKeyboardShortcut,
} from 'components/keyboard_shortcuts/keyboard_shortcuts_sequence/keyboard_shortcuts_sequence';
import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';
import classNames from 'classnames';

import styles from './styles.module.css';

type ShortcutTooltipProps = {
    descriptor: KeyboardShortcutDescriptor;
    isHidden?: boolean;
}

const DEFAULT_TOOLTIP_STATE: TooltipTriggerProps = {delay: 500, closeDelay: 0};
const DEFAULT_TRIGGER_STATE = {};

export const ShortcutTooltip: FC<PropsWithChildren<ShortcutTooltipProps>> = memo((props) => {
    const {
        children,
        descriptor,
        isHidden = false,
        ...restProps
    } = props;
    const targetRef = useRef<HTMLDivElement>(null);

    const tooltipState = useTooltipTriggerState(DEFAULT_TOOLTIP_STATE);
    const {triggerProps, tooltipProps} = useTooltipTrigger(DEFAULT_TRIGGER_STATE, tooltipState, targetRef);

    const {keys, description} = useKeyboardShortcut(descriptor);
    const [titleCls] = useTypography([{size: TypographySize.BodySTrue, noDefaultColor: true}]);

    const handleClose = () => {
        tooltipState.close();
    };

    return (
        <>
            <div
                {...triggerProps}
                ref={targetRef}
                onClick={handleClose}
                onPointerUp={handleClose}
                className={styles.wrapper}
                {...restProps}
            >
                {children}
            </div>
            {!isHidden && tooltipState.isOpen && (
                <Tooltip
                    {...tooltipProps}
                    state={tooltipState}
                    targetRef={targetRef}
                    offset={6}
                >
                    <div className={styles.wrapper}>
                        <span className={classNames(titleCls, styles.title)}>{description}</span>
                        {keys.length > 0 && (
                            <div className={styles.shortcutContainer}>
                                {keys.map((key) => (
                                    <ShortcutKey
                                        key={key}
                                        variant={ShortcutKeyVariant.Tooltip}
                                    >
                                        {key}
                                    </ShortcutKey>
                                ))}
                            </div>
                        )}
                    </div>
                </Tooltip>
            )}
        </>
    );
});
